import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  FaBuilding, 
  FaBoxOpen, 
  FaMotorcycle, 
  FaUserShield, 
  FaMoneyBillWave,
  FaChartLine,
  FaUsers,
  FaClipboardList
} from 'react-icons/fa';
import { getAllCompanies } from '../../../../store/actions/companies';
import { getAllAdmins } from '../../../../store/actions/admins';
import { axios } from '../../../../lib';
import Container from './styles';

// Admin Dashboard - New Implementation (v2)
const DashContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData } = useSelector((s) => s.UserData);
  const companyData = useSelector((s) => s.CompanyData || { data: [] });
  const adminData = useSelector((s) => s.AdminData || { data: [] });
  
  const companies = companyData.data || [];
  const admins = adminData.data || [];
  
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalOrders: 0,
    totalRiders: 0,
    totalAdmins: 0,
    systemRevenue: 0,
    activeCompanies: 0
  });
  
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [dispatch]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch companies
      await dispatch(getAllCompanies());
      
      // Fetch admins
      await dispatch(getAllAdmins());
      
      // Fetch system-wide orders
      try {
        const ordersResponse = await axios.get('/admin/orders?limit=5');
        if (ordersResponse && ordersResponse.data) {
          setRecentOrders(ordersResponse.data || []);
        }
      } catch (err) {
        console.log('Orders endpoint not available yet');
      }
      
      // Fetch stats if available
      try {
        const statsResponse = await axios.get('/admin/stats');
        if (statsResponse && statsResponse.data) {
          setStats(prev => ({
            ...prev,
            ...statsResponse.data
          }));
        }
      } catch (err) {
        console.log('Stats endpoint not available yet');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companies.length > 0) {
      setStats(prev => ({
        ...prev,
        totalCompanies: companies.length,
        activeCompanies: companies.filter(c => c.status === 'active').length
      }));
      
      // Get 5 most recent companies
      const sorted = [...companies].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setRecentCompanies(sorted.slice(0, 5));
    }
  }, [companies]);

  useEffect(() => {
    if (admins.length > 0) {
      setStats(prev => ({
        ...prev,
        totalAdmins: admins.length
      }));
    }
  }, [admins]);

  const statCards = [
    {
      icon: FaBuilding,
      label: 'Total Companies',
      value: stats.totalCompanies,
      change: `${stats.activeCompanies} active`,
      color: '#10b981',
    },
    {
      icon: FaBoxOpen,
      label: 'Total Orders',
      value: stats.totalOrders,
      change: 'All companies',
      color: '#3b82f6',
    },
    {
      icon: FaMotorcycle,
      label: 'Total Riders',
      value: stats.totalRiders,
      change: 'System-wide',
      color: '#8b5cf6',
    },
    {
      icon: FaUserShield,
      label: 'Admins',
      value: stats.totalAdmins,
      change: 'Platform admins',
      color: '#f59e0b',
    },
  ];

  const getStatusColor = (status) => {
    const colors = {
      PENDING: '#FFA726',
      ASSIGNED: '#42A5F5',
      PICKED_UP: '#7E57C2',
      IN_TRANSIT: '#66BB6A',
      DELIVERED: '#4CAF50',
      CANCELLED: '#EF5350',
      active: '#4CAF50',
      inactive: '#9E9E9E',
    };
    return colors[status] || '#9E9E9E';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes} mins ago`;
    if (hours < 24) return `${hours} hours ago`;
    return `${days} days ago`;
  };

  if (loading) {
    return (
      <Container>
        <div className="welcome-section">
          <h1>Loading Dashboard...</h1>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="welcome-section">
        <h1>Admin Dashboard</h1>
        <p>System-wide overview and management</p>
      </div>

      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className="stat-card" style={{ borderLeftColor: stat.color }}>
            <div className="stat-icon" style={{ background: `${stat.color}20`, color: stat.color }}>
              <stat.icon />
            </div>
            <div className="stat-content">
              <p className="stat-label">{stat.label}</p>
              <h2 className="stat-value">{stat.value}</h2>
              <p className="stat-change">{stat.change}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="content-grid">
        <div className="card">
          <div className="card-header">
            <h3>Recent Orders</h3>
            <button className="view-all-btn" onClick={() => navigate('/dashboard/orders')}>
              View All
            </button>
          </div>
          <div className="orders-list">
            {recentOrders.length > 0 ? (
              recentOrders.map((order) => (
                <div key={order.id} className="order-item" onClick={() => navigate('/dashboard/orders')}>
                  <div className="order-info">
                    <p className="order-tracking">
                      {order.trackingNumber || order.tracking_number}
                    </p>
                    <p className="order-customer">
                      {order.customerName || order.customer_name}
                    </p>
                  </div>
                  <div className="order-status">
                    <span 
                      className="status-badge" 
                      style={{ background: getStatusColor(order.status) }}
                    >
                      {order.status}
                    </span>
                    <span className="order-time">
                      {formatDate(order.createdAt || order.created_at)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <FaBoxOpen style={{ fontSize: '3rem', opacity: 0.3 }} />
                <p>No recent orders</p>
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Recent Companies</h3>
            <button className="view-all-btn" onClick={() => navigate('/dashboard/companies')}>
              View All
            </button>
          </div>
          <div className="companies-list">
            {recentCompanies.length > 0 ? (
              recentCompanies.map((company) => (
                <div key={company.id} className="company-item" onClick={() => navigate('/dashboard/companies')}>
                  <div className="company-info">
                    <FaBuilding className="company-icon" />
                    <div>
                      <p className="company-name">
                        {company.company_name}
                      </p>
                      <p className="company-email">
                        {company.company_email}
                      </p>
                    </div>
                  </div>
                  <div className="company-status">
                    <span 
                      className="status-badge" 
                      style={{ background: getStatusColor(company.status) }}
                    >
                      {company.status}
                    </span>
                    <span className="company-time">
                      {formatDate(company.created_at)}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <FaBuilding style={{ fontSize: '3rem', opacity: 0.3 }} />
                <p>No companies registered</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="content-grid">
        <div className="card">
          <h3>Quick Actions</h3>
          <div className="actions-list">
            <button className="action-btn" onClick={() => navigate('/dashboard/companies')}>
              <FaBuilding />
              <span>Manage Companies</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/dashboard/admins')}>
              <FaUserShield />
              <span>Manage Admins</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/dashboard/orders')}>
              <FaBoxOpen />
              <span>View All Orders</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/dashboard/audit-logs')}>
              <FaClipboardList />
              <span>Audit Logs</span>
            </button>
          </div>
        </div>

        <div className="card">
          <h3>System Overview</h3>
          <div className="system-overview">
            <div className="overview-item">
              <div className="overview-icon" style={{ background: '#10b98120', color: '#10b981' }}>
                <FaChartLine />
              </div>
              <div className="overview-content">
                <p className="overview-label">Active Companies</p>
                <p className="overview-value">{stats.activeCompanies}</p>
              </div>
            </div>
            <div className="overview-item">
              <div className="overview-icon" style={{ background: '#3b82f620', color: '#3b82f6' }}>
                <FaUsers />
              </div>
              <div className="overview-content">
                <p className="overview-label">Total Users</p>
                <p className="overview-value">{stats.totalCompanies + stats.totalAdmins}</p>
              </div>
            </div>
            <div className="overview-item">
              <div className="overview-icon" style={{ background: '#f59e0b20', color: '#f59e0b' }}>
                <FaMoneyBillWave />
              </div>
              <div className="overview-content">
                <p className="overview-label">System Revenue</p>
                <p className="overview-value">₦{stats.systemRevenue?.toLocaleString() || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default DashContent;
